/**
 * Fleet Analytics — understand the fleet, identify risk, plan replacements.
 *
 * Three sections:
 * 1. Fleet Overview — what do we have (composition, age, locations)
 * 2. Risk Exposure — what's aging out, low on resources, non-compliant
 * 3. Replacement Plan — prioritized list + budget simulator
 *
 * Features:
 * - Editable cost-per-device used in all financial calculations
 * - Every KPI number is clickable → opens drilldown modal with device list + reasons
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { ErrorMessage } from '@/components/ui/ErrorMessage';

// ─── Types ─────────────────────────────────────────────────────────────

interface DevicePriority {
  assetId: number; deviceName: string | null; serialNumber: string; model: string | null;
  manufacturer: string | null; siteNumber: string | null; assignedUser: string | null;
  ageYears: number; totalRAMGB: number | null; diskFreeGB: number | null; diskTotalGB: number | null;
  diskFreePercent: number | null; complianceState: string | null; ninjaOnline: boolean | null;
  priorityScore: number; scoreBreakdown: { age: number; ram: number; disk: number; compliance: number; offline: number };
}
interface BranchScore { siteNumber: string; branchName: string; deviceCount: number; avgScore: number; avgAge: number; devicesNeedingReplacement: number; }
interface PriorityData { totalDevices: number; devicesNeedingReplacement: number; avgScore: number; topPriority: DevicePriority[]; branchScores: BranchScore[]; budgetSimulation: { budget: number; avgCostPerDevice: number; devicesReplaceable: number } | null; security?: { nonCompliant: number; offlineOver7Days: number; lowRAM: number; lowDisk: number; osVersions: Array<{ OS: string; cnt: number }> }; compatibility?: { win11Ready: number; win11NotReady: number; ramUnknown: number; ramDistribution: Array<{ bucket: string; count: number }> }; financials?: { totalReplacementLiability: number; deferredRiskDevices: number; avgCostPerDevice: number; costPerEmployee: number; totalEmployees: number }; }
interface FleetData {
  summary: { totalComputers: number; totalWithAge: number; avgAge: number | null; overdueRefresh: number };
  ageDistribution: Array<{ bucket: string; count: number }>;
  refreshForecast: Array<{ year: number; laptops: number; desktops: number }>;
  locationScorecard: Array<{ siteNumber: string; branchName: string; deviceCount: number; laptopCount: number; desktopCount: number; employeeCount: number; ratio: number | null; avgAge: number | null }>;
  roleBreakdown: Array<{ role: string; count: number }>;
}

interface DrilldownDevice {
  AssetId: number; DeviceName: string | null; SerialNumber: string; Manufacturer: string | null;
  Model: string | null; DeviceType: string | null; DeviceRole: string | null; SiteNumber: string | null;
  AssignedUser: string | null; AcquisitionDate: string | null;
  TotalRAMGB?: number | null; DiskFreeGB?: number | null; DiskTotalGB?: number | null;
  DiskFreePercent?: number | null; IntuneComplianceState?: string | null; NinjaLastContact?: string | null;
}

interface DrilldownState {
  open: boolean;
  title: string;
  reason: string;
  devices: DrilldownDevice[];
  loading: boolean;
}

// ─── Main Component ────────────────────────────────────────────────────

export function FleetAnalytics() {
  const [budget, setBudget] = useState(50000);
  const [costPerDevice, setCostPerDevice] = useState(1200);
  const [debouncedBudget, setDebouncedBudget] = useState(50000);
  const [debouncedCost, setDebouncedCost] = useState(1200);
  const [sortBy, setSortBy] = useState<'score' | 'age' | 'ram' | 'disk'>('score');
  const [drilldown, setDrilldown] = useState<DrilldownState>({ open: false, title: '', reason: '', devices: [], loading: false });

  // Debounce budget and cost inputs — only fire query after 800ms of no typing
  const budgetTimer = useRef<ReturnType<typeof setTimeout>>();
  const costTimer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    budgetTimer.current = setTimeout(() => setDebouncedBudget(budget), 800);
    return () => clearTimeout(budgetTimer.current);
  }, [budget]);

  useEffect(() => {
    costTimer.current = setTimeout(() => setDebouncedCost(costPerDevice), 800);
    return () => clearTimeout(costTimer.current);
  }, [costPerDevice]);

  const { data: fleet, isLoading: loadingFleet, error: fleetError } = useQuery({
    queryKey: ['fleet-analytics'],
    queryFn: async () => { const r = await fetch('/api/fleet-analytics', { credentials: 'include' }); if (!r.ok) throw new Error(`${r.status}`); return r.json() as Promise<FleetData>; },
  });

  const { data: lifecycle } = useQuery({
    queryKey: ['device-lifecycle'],
    queryFn: async () => { const r = await fetch('/api/device-lifecycle', { credentials: 'include' }); if (!r.ok) return null; return r.json(); },
  });

  const { data: priority } = useQuery({
    queryKey: ['replacement-priority', debouncedBudget, debouncedCost],
    queryFn: async () => { const r = await fetch(`/api/fleet-analytics/replacement-priority?budget=${debouncedBudget}&costPerDevice=${debouncedCost}`, { credentials: 'include' }); if (!r.ok) return null; return r.json() as Promise<PriorityData>; },
  });

  // ── Drilldown fetcher ──────────────────────────────────────────────
  const openDrilldown = useCallback(async (title: string, reason: string, type: string, value?: string) => {
    setDrilldown({ open: true, title, reason, devices: [], loading: true });
    try {
      const params = new URLSearchParams({ type });
      if (value) params.set('value', value);
      const r = await fetch(`/api/fleet-analytics/drilldown?${params}`, { credentials: 'include' });
      if (!r.ok) throw new Error(`${r.status}`);
      const data = await r.json();
      setDrilldown(prev => ({ ...prev, devices: data.devices ?? [], loading: false }));
    } catch {
      setDrilldown(prev => ({ ...prev, loading: false }));
    }
  }, []);

  const closeDrilldown = useCallback(() => setDrilldown(prev => ({ ...prev, open: false })), []);

  if (loadingFleet) return <LoadingSpinner message="Loading fleet data…" />;
  if (fleetError) return <ErrorMessage message={(fleetError as Error).message} />;
  if (!fleet) return null;

  const { summary, ageDistribution, refreshForecast } = fleet;
  const sorted = priority ? [...priority.topPriority].sort((a, b) => {
    if (sortBy === 'age') return b.ageYears - a.ageYears;
    if (sortBy === 'ram') return (a.totalRAMGB ?? 999) - (b.totalRAMGB ?? 999);
    if (sortBy === 'disk') return (a.diskFreePercent ?? 999) - (b.diskFreePercent ?? 999);
    return b.priorityScore - a.priorityScore;
  }) : [];

  return (
    <div>
      <h1 className="text-lg font-semibold text-text-primary mb-1">Fleet & Lifecycle Management</h1>
      <p className="text-sm text-slate-500 mb-6">Fleet composition, lifecycle intelligence, risk exposure, and replacement planning</p>

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* SECTION 1: FLEET OVERVIEW — What do we have? */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      <h2 className="text-sm font-semibold text-slate-600 uppercase tracking-wide mb-3">Fleet Overview</h2>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <Kpi label="Total Computers" value={summary.totalComputers} onClick={() => openDrilldown('Total Computers', 'All laptops and desktops currently in use', 'total-computers')} />
        <Kpi label="With Age Data" value={summary.totalWithAge} onClick={() => openDrilldown('Devices With Age Data', 'Devices that have a known acquisition date', 'total-computers')} />
        <Kpi label="Avg Age" value={summary.avgAge ? `${summary.avgAge.toFixed(1)} yr` : '—'} onClick={() => openDrilldown('All Devices (by age)', 'Full fleet sorted by acquisition date', 'total-computers')} />
        <Kpi label="Overdue Refresh" value={summary.overdueRefresh} color="red" onClick={() => openDrilldown('Overdue for Refresh', 'Laptops >4yr or Desktops >5yr past their refresh cycle', 'overdue')} />
        {priority && <Kpi label="Avg Health Score" value={`${priority.avgScore}/100`} onClick={() => openDrilldown('Devices Needing Replacement', 'Devices with priority score ≥30', 'needs-replacement')} />}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Age Distribution */}
        <div className="bg-white rounded-lg border border-border p-5">
          <h3 className="text-sm font-semibold text-text-primary mb-3">Age Distribution</h3>
          <div className="space-y-2">
            {ageDistribution.map(a => {
              const max = Math.max(...ageDistribution.map(x => x.count));
              const isOld = a.bucket.includes('5') || a.bucket.includes('6') || a.bucket.includes('7') || a.bucket.includes('8');
              return (
                <div key={a.bucket} className="flex items-center gap-2 cursor-pointer hover:bg-slate-50 rounded px-1 -mx-1" onClick={() => openDrilldown(`Age: ${a.bucket}`, `Devices with acquisition age in the ${a.bucket} range`, 'age', a.bucket)}>
                  <span className="text-xs text-slate-600 w-16">{a.bucket}</span>
                  <div className="flex-1 h-5 bg-slate-100 rounded overflow-hidden">
                    <div className={`h-full rounded ${isOld ? 'bg-red-400' : 'bg-emerald-400'}`} style={{ width: `${(a.count / max) * 100}%` }} />
                  </div>
                  <span className="text-xs font-medium text-slate-700 w-10 text-right">{a.count}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Refresh Forecast */}
        <div className="bg-white rounded-lg border border-border p-5">
          <h3 className="text-sm font-semibold text-text-primary mb-3">Refresh Forecast (devices due by year)</h3>
          <table className="w-full text-[13px]">
            <thead><tr className="border-b-2 border-border">
              <th className="text-left py-1.5 px-2 font-medium text-slate-600">Year</th>
              <th className="text-right py-1.5 px-2 font-medium text-slate-600">Laptops</th>
              <th className="text-right py-1.5 px-2 font-medium text-slate-600">Desktops</th>
              <th className="text-right py-1.5 px-2 font-medium text-slate-600">Total</th>
              <th className="text-right py-1.5 px-2 font-medium text-slate-600">Est. Cost</th>
            </tr></thead>
            <tbody>
              {refreshForecast.map(f => (
                <tr key={f.year} className="border-b border-border-subtle cursor-pointer hover:bg-slate-50" onClick={() => openDrilldown(`Refresh Year: ${f.year}`, `Devices due for refresh in ${f.year} (Laptops @4yr, Desktops @5yr cycle)`, 'refresh', String(f.year))}>
                  <td className="py-1.5 px-2 font-medium">{f.year}</td>
                  <td className="py-1.5 px-2 text-right">{f.laptops}</td>
                  <td className="py-1.5 px-2 text-right">{f.desktops}</td>
                  <td className="py-1.5 px-2 text-right font-bold">{f.laptops + f.desktops}</td>
                  <td className="py-1.5 px-2 text-right text-slate-600">${((f.laptops + f.desktops) * costPerDevice).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* SECTION 2: RISK EXPOSURE — What's at risk? */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      {priority && (
        <>
          <h2 className="text-sm font-semibold text-slate-600 uppercase tracking-wide mb-3">Risk Exposure</h2>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <Kpi label="Need Replacement (score ≥30)" value={priority.devicesNeedingReplacement} color="red" onClick={() => openDrilldown('Devices Needing Replacement', 'Priority score ≥30 — age, RAM, disk, compliance, or offline issues', 'needs-replacement')} />
            <Kpi label="Low RAM (<8GB)" value={priority.security?.lowRAM ?? priority.topPriority.filter(d => d.totalRAMGB != null && d.totalRAMGB < 8).length} color="amber" onClick={() => openDrilldown('Low RAM Devices', 'Devices with less than 8GB RAM — performance bottleneck', 'low-ram')} />
            <Kpi label="Low Disk (<20% free)" value={priority.security?.lowDisk ?? priority.topPriority.filter(d => d.diskFreePercent != null && d.diskFreePercent < 20).length} color="amber" onClick={() => openDrilldown('Low Disk Space', 'Devices with less than 20% free disk — risk of crashes and failed updates', 'low-disk')} />
            <Kpi label="Non-Compliant" value={priority.security?.nonCompliant ?? priority.topPriority.filter(d => d.complianceState === 'noncompliant').length} color="red" onClick={() => openDrilldown('Non-Compliant Devices', 'Intune compliance policy failures — security risk', 'non-compliant')} />
          </div>

          {/* Branch Fleet Health */}
          {priority.branchScores.length > 0 && (
            <div className="bg-white rounded-lg border border-border p-5 mb-8">
              <h3 className="text-sm font-semibold text-text-primary mb-3">Branch Fleet Health (worst first)</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-[13px]">
                  <thead><tr className="border-b-2 border-border">
                    <th className="text-left py-1.5 px-2 font-medium text-slate-600">Branch</th>
                    <th className="text-right py-1.5 px-2 font-medium text-slate-600">Devices</th>
                    <th className="text-right py-1.5 px-2 font-medium text-slate-600">Avg Age</th>
                    <th className="text-right py-1.5 px-2 font-medium text-slate-600">Health Score</th>
                    <th className="text-right py-1.5 px-2 font-medium text-slate-600">Need Replace</th>
                  </tr></thead>
                  <tbody>
                    {priority.branchScores.map(b => (
                      <tr key={b.siteNumber} className="border-b border-border-subtle cursor-pointer hover:bg-slate-50" onClick={() => openDrilldown(`Branch: ${b.branchName}`, `All devices at site ${b.siteNumber} — ${b.branchName}`, 'site', b.siteNumber)}>
                        <td className="py-1.5 px-2"><Link to={`/sites/${b.siteNumber}`} className="text-baker-red no-underline hover:underline" onClick={e => e.stopPropagation()}>{b.branchName}</Link></td>
                        <td className="py-1.5 px-2 text-right">{b.deviceCount}</td>
                        <td className="py-1.5 px-2 text-right">{b.avgAge}yr</td>
                        <td className="py-1.5 px-2 text-right"><span className={`font-bold ${b.avgScore >= 30 ? 'text-red-600' : b.avgScore >= 20 ? 'text-amber-600' : 'text-emerald-600'}`}>{b.avgScore}</span></td>
                        <td className="py-1.5 px-2 text-right"><span className={b.devicesNeedingReplacement > 0 ? 'font-bold text-red-600' : ''}>{b.devicesNeedingReplacement}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ═══════════════════════════════════════════════════════════════ */}
          {/* SECTION 2B: SECURITY POSTURE */}
          {/* ═══════════════════════════════════════════════════════════════ */}
          {priority.security && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
              <div className="bg-white rounded-lg border border-border p-5">
                <h3 className="text-sm font-semibold text-text-primary mb-3">🔒 Security Posture</h3>
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="text-center p-3 bg-red-50 rounded-lg cursor-pointer hover:bg-red-100 transition-colors" onClick={() => openDrilldown('Non-Compliant Devices', 'Intune compliance policy failures', 'non-compliant')}>
                    <div className="text-xl font-bold text-red-700">{priority.security.nonCompliant}</div><div className="text-xs text-slate-500">Non-Compliant</div>
                  </div>
                  <div className="text-center p-3 bg-amber-50 rounded-lg cursor-pointer hover:bg-amber-100 transition-colors" onClick={() => openDrilldown('Offline 15+ Days', 'Devices not reporting to NinjaOne for over 15 days — may be lost, stolen, or broken', 'offline')}>
                    <div className="text-xl font-bold text-amber-700">{priority.security.offlineOver7Days}</div><div className="text-xs text-slate-500">Offline 15+ Days</div>
                  </div>
                  <div className="text-center p-3 bg-amber-50 rounded-lg cursor-pointer hover:bg-amber-100 transition-colors" onClick={() => openDrilldown('Low RAM Devices', 'Devices with less than 8GB RAM', 'low-ram')}>
                    <div className="text-xl font-bold text-amber-700">{priority.security.lowRAM}</div><div className="text-xs text-slate-500">Low RAM (&lt;8GB)</div>
                  </div>
                  <div className="text-center p-3 bg-amber-50 rounded-lg cursor-pointer hover:bg-amber-100 transition-colors" onClick={() => openDrilldown('Low Disk Space', 'Devices with less than 20% free disk space', 'low-disk')}>
                    <div className="text-xl font-bold text-amber-700">{priority.security.lowDisk}</div><div className="text-xs text-slate-500">Low Disk (&lt;20%)</div>
                  </div>
                </div>
                {priority.security.osVersions.length > 0 && (
                  <>
                    <h4 className="text-xs font-medium text-slate-500 mb-2">OS Version Distribution</h4>
                    <div className="space-y-1">
                      {priority.security.osVersions.slice(0, 6).map(os => (
                        <div key={os.OS} className="flex items-center gap-2 text-xs">
                          <span className="text-slate-600 w-48 truncate">{os.OS}</span>
                          <span className="font-medium text-slate-700">{os.cnt}</span>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>

              <div className="bg-white rounded-lg border border-border p-5">
                <h3 className="text-sm font-semibold text-text-primary mb-3">💻 App Compatibility (Windows 11 Readiness)</h3>
                {priority.compatibility && (
                  <>
                    <div className="grid grid-cols-3 gap-3 mb-4">
                      <div className="text-center p-3 bg-emerald-50 rounded-lg cursor-pointer hover:bg-emerald-100 transition-colors" onClick={() => openDrilldown('Win11 Ready (≥8GB RAM)', 'Devices with 8GB+ RAM that meet Windows 11 minimum requirements', 'total-computers')}>
                        <div className="text-xl font-bold text-emerald-700">{priority.compatibility.win11Ready}</div><div className="text-xs text-slate-500">Ready (≥8GB)</div>
                      </div>
                      <div className="text-center p-3 bg-red-50 rounded-lg cursor-pointer hover:bg-red-100 transition-colors" onClick={() => openDrilldown('Win11 Not Ready (<8GB RAM)', 'Devices with less than 8GB RAM — cannot run Windows 11 effectively', 'low-ram')}>
                        <div className="text-xl font-bold text-red-700">{priority.compatibility.win11NotReady}</div><div className="text-xs text-slate-500">Not Ready</div>
                      </div>
                      <div className="text-center p-3 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-200 transition-colors" onClick={() => openDrilldown('Unknown RAM', 'Devices where RAM data is not available', 'total-computers')}>
                        <div className="text-xl font-bold text-slate-600">{priority.compatibility.ramUnknown}</div><div className="text-xs text-slate-500">Unknown</div>
                      </div>
                    </div>
                    <h4 className="text-xs font-medium text-slate-500 mb-2">RAM Distribution</h4>
                    <div className="space-y-2">
                      {priority.compatibility.ramDistribution.map(r => {
                        const max = Math.max(...priority.compatibility!.ramDistribution.map(x => x.count));
                        return (
                          <div key={r.bucket} className="flex items-center gap-2">
                            <span className="text-xs text-slate-600 w-16">{r.bucket}</span>
                            <div className="flex-1 h-4 bg-slate-100 rounded overflow-hidden"><div className={`h-full rounded ${r.bucket.includes('<') || r.bucket.includes('4-7') ? 'bg-red-400' : 'bg-emerald-400'}`} style={{ width: `${(r.count / max) * 100}%` }} /></div>
                            <span className="text-xs font-medium w-10 text-right">{r.count}</span>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {/* ═══════════════════════════════════════════════════════════════ */}
          {/* SECTION 2C: FP&A / BUDGET JUSTIFICATION */}
          {/* ═══════════════════════════════════════════════════════════════ */}
          {priority.financials && (
            <div className="bg-white rounded-lg border border-border p-5 mb-8">
              <h3 className="text-sm font-semibold text-text-primary mb-3">📊 Budget Justification (for FP&A)</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center p-3 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100 transition-colors" onClick={() => openDrilldown('Devices Needing Replacement', 'All devices with priority score ≥30 contributing to replacement liability', 'needs-replacement')}>
                  <div className="text-xl font-bold text-text-primary">${(priority.devicesNeedingReplacement * costPerDevice).toLocaleString()}</div><div className="text-xs text-slate-500">Total Replacement Liability</div>
                </div>
                <div className="text-center p-3 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100 transition-colors" onClick={() => openDrilldown('Deferred Risk (5+ Years)', 'Devices over 5 years old — well past recommended refresh cycle', 'age', '5+ yr')}>
                  <div className="text-xl font-bold text-red-700">{priority.financials.deferredRiskDevices}</div><div className="text-xs text-slate-500">Devices 5+ Years (deferred risk)</div>
                </div>
                <div className="text-center p-3 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100 transition-colors" onClick={() => openDrilldown('All Fleet Devices', 'Full fleet used to calculate cost per employee', 'total-computers')}>
                  <div className="text-xl font-bold text-text-primary">${Math.round((priority.totalDevices * costPerDevice) / (priority.financials.totalEmployees || 1)).toLocaleString()}</div><div className="text-xs text-slate-500">Hardware Cost / Employee</div>
                </div>
                <div className="text-center p-3 bg-slate-50 rounded-lg">
                  <div className="text-xl font-bold text-text-primary">${costPerDevice.toLocaleString()}</div><div className="text-xs text-slate-500">Avg Replacement Cost</div>
                </div>
              </div>
              <p className="text-xs text-slate-500 mt-3">Deferred replacement increases support costs, security risk, and productivity loss. Each year of deferral adds ~$200/device in additional support burden.</p>
            </div>
          )}

          {/* ═══════════════════════════════════════════════════════════════ */}
          {/* SECTION 3: REPLACEMENT PLAN — What do we replace? */}
          {/* ═══════════════════════════════════════════════════════════════ */}
          <h2 className="text-sm font-semibold text-slate-600 uppercase tracking-wide mb-3">Replacement Plan</h2>

          {/* Budget Simulator with editable cost-per-device */}
          <div className="bg-baker-red/5 rounded-lg border border-baker-red/20 p-5 mb-6">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <h3 className="text-sm font-semibold text-text-primary">Budget Simulator</h3>
                <p className="text-xs text-slate-500">Enter your refresh budget and cost per device — green rows below show what you can replace</p>
              </div>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-1.5">
                  <label className="text-xs text-slate-500">Budget</label>
                  <div className="flex items-center">
                    <span className="text-sm text-slate-600">$</span>
                    <input type="number" value={budget} onChange={(e) => setBudget(Number(e.target.value))} step={5000} min={0} className="py-2 px-3 rounded-lg border border-border text-sm w-28 bg-white font-medium ml-1" />
                  </div>
                </div>
                <div className="flex items-center gap-1.5">
                  <label className="text-xs text-slate-500">Cost/Device</label>
                  <div className="flex items-center">
                    <span className="text-sm text-slate-600">$</span>
                    <input type="number" value={costPerDevice} onChange={(e) => setCostPerDevice(Number(e.target.value) || 1200)} step={50} min={100} className="py-2 px-3 rounded-lg border border-border text-sm w-24 bg-white font-medium ml-1" />
                  </div>
                </div>
              </div>
            </div>
            {priority.budgetSimulation && (
              <div className="mt-3 flex items-center gap-6">
                <span className="text-sm text-slate-700">With <strong>${budget.toLocaleString()}</strong> @ <strong>${costPerDevice.toLocaleString()}</strong>/device you replace <strong className="text-baker-red text-lg">{Math.floor(budget / costPerDevice)}</strong> of {priority.devicesNeedingReplacement} devices needing replacement.</span>
              </div>
            )}
          </div>

          {/* Replace-First Table */}
          <div className="bg-white rounded-lg border border-border p-5 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-text-primary">Replace First (ranked by priority)</h3>
              <select value={sortBy} onChange={(e) => setSortBy(e.target.value as typeof sortBy)} className="py-1 px-2 rounded border border-border text-xs bg-white">
                <option value="score">Priority Score</option>
                <option value="age">Age (oldest)</option>
                <option value="ram">RAM (lowest)</option>
                <option value="disk">Disk Free (lowest)</option>
              </select>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-[13px]">
                <thead><tr className="border-b-2 border-border">
                  <th className="text-left py-2 px-2 font-medium text-slate-600">Device</th>
                  <th className="text-left py-2 px-2 font-medium text-slate-600">Model</th>
                  <th className="text-right py-2 px-2 font-medium text-slate-600">Score</th>
                  <th className="text-right py-2 px-2 font-medium text-slate-600">Age</th>
                  <th className="text-right py-2 px-2 font-medium text-slate-600">RAM</th>
                  <th className="text-right py-2 px-2 font-medium text-slate-600">Disk Free</th>
                  <th className="text-left py-2 px-2 font-medium text-slate-600">Site</th>
                  <th className="text-left py-2 px-2 font-medium text-slate-600">User</th>
                </tr></thead>
                <tbody>
                  {sorted.slice(0, 50).map((d, i) => {
                    const inBudget = budget > 0 && costPerDevice > 0 && i < Math.floor(budget / costPerDevice);
                    return (
                      <tr key={d.assetId} className={`border-b border-border-subtle ${inBudget ? 'bg-emerald-50/50' : ''}`}>
                        <td className="py-2 px-2"><Link to={`/assets/${d.assetId}`} className="text-baker-red no-underline hover:underline font-medium">{d.deviceName || d.serialNumber}</Link></td>
                        <td className="py-2 px-2 text-slate-600 text-xs">{d.model || '—'}</td>
                        <td className="py-2 px-2 text-right"><span className={`font-bold ${d.priorityScore >= 50 ? 'text-red-600' : d.priorityScore >= 30 ? 'text-amber-600' : 'text-slate-600'}`}>{d.priorityScore}</span></td>
                        <td className="py-2 px-2 text-right"><span className={d.ageYears >= 5 ? 'text-red-600 font-bold' : ''}>{d.ageYears > 0 ? `${d.ageYears}yr` : '—'}</span></td>
                        <td className="py-2 px-2 text-right"><span className={d.totalRAMGB != null && d.totalRAMGB < 8 ? 'text-red-600 font-bold' : ''}>{d.totalRAMGB != null ? `${d.totalRAMGB}GB` : '—'}</span></td>
                        <td className="py-2 px-2 text-right"><span className={d.diskFreePercent != null && d.diskFreePercent < 20 ? 'text-red-600 font-bold' : ''}>{d.diskFreePercent != null ? `${d.diskFreePercent}%` : d.diskFreeGB != null ? `${Math.round(d.diskFreeGB)}GB` : '—'}</span></td>
                        <td className="py-2 px-2">{d.siteNumber ? <Link to={`/sites/${d.siteNumber}`} className="text-baker-red no-underline hover:underline text-xs">{d.siteNumber}</Link> : '—'}</td>
                        <td className="py-2 px-2 text-slate-600 text-xs truncate max-w-[120px]">{d.assignedUser || '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {budget > 0 && costPerDevice > 0 && Math.floor(budget / costPerDevice) > 0 && (
              <p className="text-xs text-emerald-600 mt-3">Green rows = within your ${budget.toLocaleString()} budget ({Math.floor(budget / costPerDevice)} devices @ ${costPerDevice.toLocaleString()}/each)</p>
            )}
          </div>

          {/* Score Legend */}
          <div className="bg-slate-50 rounded-lg border border-border p-5 text-xs text-slate-600">
            <h4 className="text-sm font-semibold text-text-primary mb-3">Priority Score Legend (0–100 pts)</h4>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4">
              <div className="bg-white rounded-lg p-3 border border-border">
                <div className="font-semibold text-slate-700 mb-1">🕐 Age <span className="text-slate-400 font-normal">(0–30 pts)</span></div>
                <ul className="space-y-0.5 text-slate-500 list-none pl-0">
                  <li>&lt;2 yr = 0 pts</li>
                  <li>2–3 yr = 10 pts</li>
                  <li>3–4 yr = 20 pts</li>
                  <li>5+ yr = 30 pts (max)</li>
                </ul>
              </div>
              <div className="bg-white rounded-lg p-3 border border-border">
                <div className="font-semibold text-slate-700 mb-1">🧠 RAM <span className="text-slate-400 font-normal">(0–25 pts)</span></div>
                <ul className="space-y-0.5 text-slate-500 list-none pl-0">
                  <li>&lt;8 GB = 25 pts</li>
                  <li>8–12 GB = 15 pts</li>
                  <li>12–16 GB = 5 pts</li>
                  <li>16+ GB = 0 pts</li>
                </ul>
              </div>
              <div className="bg-white rounded-lg p-3 border border-border">
                <div className="font-semibold text-slate-700 mb-1">💾 Disk Free <span className="text-slate-400 font-normal">(0–25 pts)</span></div>
                <ul className="space-y-0.5 text-slate-500 list-none pl-0">
                  <li>&lt;10% free = 25 pts</li>
                  <li>10–20% = 15 pts</li>
                  <li>20–30% = 5 pts</li>
                  <li>30%+ = 0 pts</li>
                </ul>
              </div>
              <div className="bg-white rounded-lg p-3 border border-border">
                <div className="font-semibold text-slate-700 mb-1">🔒 Compliance <span className="text-slate-400 font-normal">(0–10 pts)</span></div>
                <ul className="space-y-0.5 text-slate-500 list-none pl-0">
                  <li>Non-compliant = 10 pts</li>
                  <li>Compliant = 0 pts</li>
                </ul>
              </div>
              <div className="bg-white rounded-lg p-3 border border-border">
                <div className="font-semibold text-slate-700 mb-1">📡 Offline <span className="text-slate-400 font-normal">(0–10 pts)</span></div>
                <ul className="space-y-0.5 text-slate-500 list-none pl-0">
                  <li>Offline 15+ days = 10 pts</li>
                  <li>Online = 0 pts</li>
                </ul>
              </div>
            </div>
            <div className="flex items-center gap-4 pt-2 border-t border-border">
              <span className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 rounded-sm bg-red-500"></span><span>Score ≥50 — Critical</span></span>
              <span className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 rounded-sm bg-amber-500"></span><span>Score 30–49 — Needs Replacement</span></span>
              <span className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 rounded-sm bg-slate-400"></span><span>Score &lt;30 — Acceptable</span></span>
            </div>
          </div>
        </>
      )}

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* SECTION 4: DEVICE LIFECYCLE INTELLIGENCE */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      {lifecycle && (
        <>
          <h2 className="text-sm font-semibold text-slate-600 uppercase tracking-wide mb-3 mt-8">Device Lifecycle Intelligence</h2>

          {/* Lifecycle KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-lg border border-border p-4 text-center cursor-pointer hover:border-baker-accent/40 transition-all" onClick={() => openDrilldown('Idle Devices', 'Online and reporting but no logged-in user — may be unused', 'idle')}>
              <div className="text-xl font-bold text-amber-600">{lifecycle.idleDevices?.length ?? 0}</div>
              <div className="text-xs text-slate-500">Idle (online, no user)</div>
            </div>
            <div className="bg-white rounded-lg border border-border p-4 text-center cursor-pointer hover:border-baker-accent/40 transition-all" onClick={() => openDrilldown('Decommission Candidates', 'Offline 90+ days while marked In Use — likely lost or broken', 'decommission')}>
              <div className="text-xl font-bold text-red-600">{lifecycle.decommissionCandidates?.length ?? 0}</div>
              <div className="text-xs text-slate-500">Decommission Candidates</div>
            </div>
            <div className="bg-white rounded-lg border border-border p-4 text-center cursor-pointer hover:border-baker-accent/40 transition-all" onClick={() => openDrilldown('Former Employee Devices', 'Assigned to people not in employee records — needs recovery', 'former-employee')}>
              <div className="text-xl font-bold text-purple-600">{lifecycle.formerEmployeeDevices?.length ?? 0}</div>
              <div className="text-xs text-slate-500">Former Employee Devices</div>
            </div>
            <div className="bg-white rounded-lg border border-border p-4 text-center cursor-pointer hover:border-baker-accent/40 transition-all" onClick={() => openDrilldown('Redeployable Devices', 'Idle + recoverable devices that could be reassigned instead of buying new', 'idle')}>
              <div className="text-xl font-bold text-emerald-600">{lifecycle.redeployable ?? 0}</div>
              <div className="text-xs text-slate-500">Redeployable (~${((lifecycle.redeployable ?? 0) * costPerDevice).toLocaleString()})</div>
            </div>
          </div>

          {/* Idle Devices */}
          {lifecycle.idleDevices?.length > 0 && (
            <div className="bg-white rounded-lg border border-border p-5 mb-6">
              <h3 className="text-sm font-semibold text-text-primary mb-1">Idle Devices ({lifecycle.idleDevices.length})</h3>
              <p className="text-xs text-slate-500 mb-3">Online and reporting but no logged-in user. May be unused or between assignments.</p>
              <div className="overflow-y-auto max-h-[250px]">
                <table className="w-full border-collapse text-[13px]">
                  <thead className="sticky top-0 bg-white"><tr className="border-b-2 border-border">
                    <th className="text-left py-2 px-2 font-medium text-slate-600">Device</th>
                    <th className="text-left py-2 px-2 font-medium text-slate-600">Model</th>
                    <th className="text-left py-2 px-2 font-medium text-slate-600">Site</th>
                    <th className="text-left py-2 px-2 font-medium text-slate-600">Assigned To</th>
                  </tr></thead>
                  <tbody>
                    {(lifecycle.idleDevices as Array<{AssetId: number; DeviceName: string; SerialNumber: string; Model: string; SiteNumber: string; AssignedUser: string}>).map((d: {AssetId: number; DeviceName: string; SerialNumber: string; Model: string; SiteNumber: string; AssignedUser: string}) => (
                      <tr key={d.AssetId} className="border-b border-border-subtle">
                        <td className="py-1.5 px-2"><Link to={`/assets/${d.AssetId}`} className="text-baker-red no-underline hover:underline font-medium">{d.DeviceName || d.SerialNumber}</Link></td>
                        <td className="py-1.5 px-2 text-xs text-slate-600">{d.Model}</td>
                        <td className="py-1.5 px-2">{d.SiteNumber ? <Link to={`/sites/${d.SiteNumber}`} className="text-baker-red no-underline hover:underline">{d.SiteNumber}</Link> : '—'}</td>
                        <td className="py-1.5 px-2">{d.AssignedUser || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Site Rebalancing */}
          {(lifecycle.siteBalance?.surplusSites?.length > 0 || lifecycle.siteBalance?.deficitSites?.length > 0) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              {lifecycle.siteBalance.surplusSites?.length > 0 && (
                <div className="bg-white rounded-lg border border-border p-5">
                  <h3 className="text-sm font-semibold text-text-primary mb-1">Surplus Sites ({lifecycle.siteBalance.surplusSites.length})</h3>
                  <p className="text-xs text-slate-500 mb-3">Ratio &gt; 1.5 — more devices than people. Transfer candidates.</p>
                  <div className="overflow-y-auto max-h-[200px]">
                    <table className="w-full border-collapse text-[12px]">
                      <thead className="sticky top-0 bg-white"><tr className="border-b border-border">
                        <th className="text-left py-1.5 px-2 font-medium text-slate-600">Site</th>
                        <th className="text-right py-1.5 px-2 font-medium text-slate-600">Devices</th>
                        <th className="text-right py-1.5 px-2 font-medium text-slate-600">Employees</th>
                        <th className="text-right py-1.5 px-2 font-medium text-slate-600">Ratio</th>
                      </tr></thead>
                      <tbody>
                        {(lifecycle.siteBalance.surplusSites as Array<{siteNumber: string; branchName: string; devices: number; employees: number; ratio: number}>).map((s: {siteNumber: string; branchName: string; devices: number; employees: number; ratio: number}) => (
                          <tr key={s.siteNumber} className="border-b border-border-subtle">
                            <td className="py-1 px-2"><Link to={`/sites/${s.siteNumber}`} className="text-baker-red no-underline hover:underline">{s.siteNumber} - {s.branchName}</Link></td>
                            <td className="py-1 px-2 text-right">{s.devices}</td>
                            <td className="py-1 px-2 text-right">{s.employees}</td>
                            <td className="py-1 px-2 text-right font-medium text-amber-600">{s.ratio?.toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              {lifecycle.siteBalance.deficitSites?.length > 0 && (
                <div className="bg-white rounded-lg border border-border p-5">
                  <h3 className="text-sm font-semibold text-text-primary mb-1">Deficit Sites ({lifecycle.siteBalance.deficitSites.length})</h3>
                  <p className="text-xs text-slate-500 mb-3">Ratio &lt; 0.5 — fewer devices than expected. May need transfers.</p>
                  <div className="overflow-y-auto max-h-[200px]">
                    <table className="w-full border-collapse text-[12px]">
                      <thead className="sticky top-0 bg-white"><tr className="border-b border-border">
                        <th className="text-left py-1.5 px-2 font-medium text-slate-600">Site</th>
                        <th className="text-right py-1.5 px-2 font-medium text-slate-600">Devices</th>
                        <th className="text-right py-1.5 px-2 font-medium text-slate-600">Employees</th>
                        <th className="text-right py-1.5 px-2 font-medium text-slate-600">Ratio</th>
                      </tr></thead>
                      <tbody>
                        {(lifecycle.siteBalance.deficitSites as Array<{siteNumber: string; branchName: string; devices: number; employees: number; ratio: number}>).map((s: {siteNumber: string; branchName: string; devices: number; employees: number; ratio: number}) => (
                          <tr key={s.siteNumber} className="border-b border-border-subtle">
                            <td className="py-1 px-2"><Link to={`/sites/${s.siteNumber}`} className="text-baker-red no-underline hover:underline">{s.siteNumber} - {s.branchName}</Link></td>
                            <td className="py-1 px-2 text-right">{s.devices}</td>
                            <td className="py-1 px-2 text-right">{s.employees}</td>
                            <td className="py-1 px-2 text-right font-medium text-red-600">{s.ratio?.toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* DRILLDOWN MODAL */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      {drilldown.open && (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-16 px-4" onClick={closeDrilldown}>
          <div className="fixed inset-0 bg-black/40" />
          <div className="relative bg-white rounded-xl shadow-2xl border border-border w-full max-w-4xl max-h-[75vh] flex flex-col" onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-border shrink-0">
              <div>
                <h2 className="text-base font-semibold text-text-primary">{drilldown.title}</h2>
                <p className="text-xs text-slate-500 mt-0.5">{drilldown.reason}</p>
              </div>
              <button onClick={closeDrilldown} className="text-slate-400 hover:text-slate-600 text-xl leading-none p-1" aria-label="Close">✕</button>
            </div>
            {/* Body */}
            <div className="overflow-auto flex-1 px-6 py-4">
              {drilldown.loading ? (
                <div className="flex items-center justify-center py-12"><LoadingSpinner message="Loading devices…" /></div>
              ) : drilldown.devices.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-8">No devices found.</p>
              ) : (
                <table className="w-full text-[13px]">
                  <thead><tr className="border-b-2 border-border sticky top-0 bg-white">
                    <th className="text-left py-2 px-2 font-medium text-slate-600">Device</th>
                    <th className="text-left py-2 px-2 font-medium text-slate-600">Model</th>
                    <th className="text-left py-2 px-2 font-medium text-slate-600">Type</th>
                    <th className="text-left py-2 px-2 font-medium text-slate-600">Site</th>
                    <th className="text-left py-2 px-2 font-medium text-slate-600">User</th>
                    <th className="text-left py-2 px-2 font-medium text-slate-600">Acquired</th>
                    <th className="text-left py-2 px-2 font-medium text-slate-600">Reason</th>
                  </tr></thead>
                  <tbody>
                    {drilldown.devices.map(d => (
                      <tr key={d.AssetId} className="border-b border-border-subtle hover:bg-slate-50">
                        <td className="py-2 px-2"><Link to={`/assets/${d.AssetId}`} className="text-baker-red no-underline hover:underline font-medium" onClick={closeDrilldown}>{d.DeviceName || d.SerialNumber}</Link></td>
                        <td className="py-2 px-2 text-slate-600 text-xs">{d.Model || '—'}</td>
                        <td className="py-2 px-2 text-xs">{d.DeviceType || '—'}</td>
                        <td className="py-2 px-2 text-xs">{d.SiteNumber || '—'}</td>
                        <td className="py-2 px-2 text-slate-600 text-xs truncate max-w-[120px]">{d.AssignedUser || '—'}</td>
                        <td className="py-2 px-2 text-xs text-slate-500">{d.AcquisitionDate ? new Date(d.AcquisitionDate).toLocaleDateString() : '—'}</td>
                        <td className="py-2 px-2 text-xs">{getDrilldownReason(d, drilldown.title)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            {/* Footer */}
            <div className="px-6 py-3 border-t border-border text-xs text-slate-500 shrink-0 flex items-center justify-between">
              <span>{drilldown.devices.length} device{drilldown.devices.length !== 1 ? 's' : ''}</span>
              <button onClick={closeDrilldown} className="px-3 py-1.5 rounded border border-border text-xs hover:bg-slate-50">Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Helper: determine reason text for drilldown row ───────────────────

function getDrilldownReason(d: DrilldownDevice, title: string): string {
  if (title.includes('Low RAM')) return d.TotalRAMGB != null ? `${d.TotalRAMGB}GB RAM` : 'Low RAM';
  if (title.includes('Low Disk') || title.includes('Disk Space')) {
    if (d.DiskFreePercent != null) return `${d.DiskFreePercent}% free`;
    if (d.DiskFreeGB != null && d.DiskTotalGB != null) return `${Math.round(d.DiskFreeGB)}/${Math.round(d.DiskTotalGB)}GB`;
    return 'Low disk';
  }
  if (title.includes('Non-Compliant')) return 'Intune: non-compliant';
  if (title.includes('Offline')) return d.NinjaLastContact ? `Last seen ${new Date(d.NinjaLastContact).toLocaleDateString()}` : 'Offline 15+ days';
  if (title.includes('Overdue') || title.includes('Deferred')) {
    if (d.AcquisitionDate) {
      const age = ((Date.now() - new Date(d.AcquisitionDate).getTime()) / (365.25 * 86400000)).toFixed(1);
      return `${age} yr old`;
    }
    return 'Past refresh cycle';
  }
  if (title.includes('Refresh Year')) {
    if (d.AcquisitionDate) {
      const age = ((Date.now() - new Date(d.AcquisitionDate).getTime()) / (365.25 * 86400000)).toFixed(1);
      return `${d.DeviceType} — ${age}yr old`;
    }
    return d.DeviceType || 'Due for refresh';
  }
  if (title.includes('Age:')) {
    if (d.AcquisitionDate) return new Date(d.AcquisitionDate).toLocaleDateString();
    return '—';
  }
  if (title.includes('Branch:')) return d.DeviceRole || d.DeviceType || '—';
  if (title.includes('Replacement') || title.includes('Needing')) {
    if (d.AcquisitionDate) {
      const age = ((Date.now() - new Date(d.AcquisitionDate).getTime()) / (365.25 * 86400000)).toFixed(1);
      return `Score ≥30 — ${age}yr old`;
    }
    return 'Score ≥30';
  }
  // Default
  return d.DeviceRole || d.DeviceType || '—';
}

// ─── KPI Card (clickable) ──────────────────────────────────────────────

function Kpi({ label, value, color, onClick }: { label: string; value: string | number; color?: 'red' | 'amber'; onClick?: () => void }) {
  const textColor = color === 'red' ? 'text-red-700' : color === 'amber' ? 'text-amber-700' : 'text-text-primary';
  return (
    <div className={`bg-white rounded-lg border border-border p-4 ${onClick ? 'cursor-pointer hover:border-baker-red/40 hover:shadow-sm transition-all' : ''}`} onClick={onClick}>
      <div className="text-xs text-slate-500">{label}</div>
      <div className={`text-2xl font-bold mt-1 ${textColor}`}>{typeof value === 'number' ? value.toLocaleString() : value}</div>
      {onClick && <div className="text-[10px] text-slate-400 mt-1">Click to view devices →</div>}
    </div>
  );
}


